import { Activity, Library, LogOut, MoreVertical, Music, Radio, Tags, Wrench } from 'lucide-react'
import { type Dispatch, type ReactNode, type SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet } from 'react-router-dom'
import type { AdminUser } from '../api/auth'
import { MavolyraLogo } from '../components/MavolyraLogo'
import { IconButton } from '../components/ui'
import packageJson from '../../../../package.json'

type AdminLayoutProps = {
  onLogout: () => Promise<void>
  user: AdminUser
}

export type AdminOutletContext = {
  closeMobileActions: () => void
  setMobileMenuItems: Dispatch<SetStateAction<ReactNode>>
}

const getNavClassName = ({ isActive }: { isActive: boolean }): string =>
  isActive ? 'sidebar-link active' : 'sidebar-link'

const getBottomNavClassName = ({ isActive }: { isActive: boolean }): string =>
  isActive ? 'mobile-bottom-nav-link active' : 'mobile-bottom-nav-link'

export const AdminLayout = ({ onLogout, user }: AdminLayoutProps) => {
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false)
  const [mobileMenuItems, setMobileMenuItems] = useState<ReactNode>(null)
  const mobileActionsRef = useRef<HTMLDivElement | null>(null)
  const closeMobileActions = useCallback((): void => setMobileActionsOpen(false), [])

  useEffect(() => {
    if (!mobileActionsOpen) {
      return undefined
    }

    const closeOnPointerDown = (event: PointerEvent): void => {
      const target = event.target

      if (target instanceof Node && mobileActionsRef.current?.contains(target)) {
        return
      }

      setMobileActionsOpen(false)
    }

    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setMobileActionsOpen(false)
      }
    }

    document.addEventListener('pointerdown', closeOnPointerDown)
    document.addEventListener('keydown', closeOnEscape)

    return () => {
      document.removeEventListener('pointerdown', closeOnPointerDown)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [mobileActionsOpen])

  return (
  <main className="app-shell">
    <header className="topnav">
      <Link className="brand" to="/channels">
        <MavolyraLogo showAdminLabel />
      </Link>
      <div className="nav-spacer" />
      <span className="user-chip">
        <span className="avatar">{user.fullName.slice(0, 1).toUpperCase()}</span>
        <span className="user-email">{user.email}</span>
      </span>
      <IconButton
        className="admin-logout-button"
        icon={<LogOut size={18} />}
        label="Log out"
        onClick={() => void onLogout()}
        title="Log out"
      />
      <div className="mobile-actions-menu" ref={mobileActionsRef}>
        <IconButton
          aria-expanded={mobileActionsOpen}
          aria-haspopup="menu"
          className="mobile-actions-trigger"
          icon={<MoreVertical size={20} />}
          label="Open admin actions"
          onClick={() => setMobileActionsOpen((open) => !open)}
        />
        {mobileActionsOpen && (
          <div className="mobile-actions-popover" role="menu">
            {mobileMenuItems && (
              <div className="mobile-actions-section">
                {mobileMenuItems}
              </div>
            )}
            <button
              className="mobile-actions-item"
              onClick={() => {
                setMobileActionsOpen(false)
                void onLogout()
              }}
              role="menuitem"
              type="button"
            >
              <LogOut size={17} />
              <span>Log out</span>
            </button>
          </div>
        )}
      </div>
    </header>

    <div className="layout">
      <aside className="sidebar" aria-label="Admin sections">
        <div className="sidebar-nav">
          <p className="sidebar-title">
            <span>Workspace</span>
            <span className="sidebar-version">v{packageJson.version}</span>
          </p>
          <NavLink className={getNavClassName} to="/admin/channels">
            <Radio size={18} />Channels
          </NavLink>
          <NavLink className={getNavClassName} to="/admin/visuals">
            <Library size={18} />Visuals
          </NavLink>
          <NavLink className={getNavClassName} to="/admin/audio">
            <Music size={18} />Audio
          </NavLink>
          <NavLink className={getNavClassName} to="/admin/labels">
            <Tags size={18} />Labels
          </NavLink>
          <NavLink className={getNavClassName} to="/admin/listening-signals">
            <Activity size={18} />Signals
          </NavLink>
          <NavLink className={getNavClassName} to="/admin/tools">
            <Wrench size={18} />Tools
          </NavLink>
        </div>
      </aside>

      <section className="main">
        <Outlet context={{ closeMobileActions, setMobileMenuItems } satisfies AdminOutletContext} />
      </section>
    </div>
    <nav className="mobile-bottom-nav" aria-label="Admin sections">
      <NavLink className={getBottomNavClassName} to="/admin/channels">
        <Radio size={18} />
        <span>Channels</span>
      </NavLink>
      <NavLink className={getBottomNavClassName} to="/admin/visuals">
        <Library size={18} />
        <span>Visuals</span>
      </NavLink>
      <NavLink className={getBottomNavClassName} to="/admin/audio">
        <Music size={18} />
        <span>Audio</span>
      </NavLink>
      <NavLink className={getBottomNavClassName} to="/admin/labels">
        <Tags size={18} />
        <span>Labels</span>
      </NavLink>
      <NavLink className={getBottomNavClassName} to="/admin/listening-signals">
        <Activity size={18} />
        <span>Signals</span>
      </NavLink>
      <NavLink className={getBottomNavClassName} to="/admin/tools">
        <Wrench size={18} />
        <span>Tools</span>
      </NavLink>
    </nav>
  </main>
  )
}
